<template>
  <div class="container-fluid">
    <Toolbar class="m-2">
      <template #start>
        <h2>{{ $t('nav.office') }}</h2>
      </template>
      <template #end>
        <Button
          :label="$t('officepage.createOffice')"
          icon="pi pi-plus"
          class=" mr-2 p-button-outlined p-button-rounded"
          @click="toOfficeForm()"
        />
      </template>
    </Toolbar>

    <DataTable
      v-model:sortField="officeFilter.pageable.sortField"
      v-model:sortOrder="officeFilter.pageable.sortOrder"
      v-model:filters="officeFilter.filters"
      v-model:rows="officeFilter.pageable.size"
      striped-rows
      row-hover
      lazy
      paginator
      paginator-position="both"
      :total-records="totalOfficeCount"
      :value="offices"
      :loading="isLoading"
      responsive-layout="scroll"
      filter-display="row"
      class="p-datatable-sm"
      @sort="getOffices"
      @filter="getOffices"
      @page="onPageChange($event)"
    >
      <Column
        :exportable="false"
        style="width: 175px; min-width: 175px;"
      >
        <template #body="{data}">
          <Button
            icon="pi pi-eye"
            class="p-button-rounded p-button-secondary mx-1"
            :title="$t('officepage.title.detail')"
            @click="showOfficeDetail(data)"
          />

          <Button
            icon="pi pi-pencil"
            class="p-button-rounded mx-1"
            :title="$t('action.crud.update')"
            @click="toOfficeForm(data)"
          />

          <Button
            icon="pi pi-trash"
            class="p-button-rounded p-button-danger mx-1"
            :title="$t('officepage.title.delete')"
            @click="showDelete(data)"
          />
        </template>
      </Column>
      <Column
        field="name"
        :header="$t('entity.office.name')"
        :sortable="true"
        :show-filter-menu="false"
      >
        <template #filter="{filterModel,filterCallback}">
          <InputText
            v-model="filterModel.value"
            type="text"
            class="p-column-filter"
            @keydown.enter="filterCallback()"
          />
        </template>
        <template #body="{data}">
          {{ data.name }}
        </template>
      </Column>

      <Column
        field="sitesId"
        :header="$t('entity.office.site')"
        :sortable="true"
        :show-filter-menu="false"
        sort-field="site.code"
      >
        <template #body="{data}">
          {{ data.site && data.site.name }}
        </template>
        <template #filter="{filterModel,filterCallback}">
          <MultiSelect
            v-model="filterModel.value"
            :options="sortedSites"
            :option-label="(site) => site.name"
            option-value="id"
            @change="filterCallback"
          />
        </template>
      </Column>

      <Column
        field="enabled"
        :header="$t('entity.office.enabled')"
        :sortable="false"
        :show-filter-menu="false"
        class="col-1"
      >
        <template #filter="{filterModel,filterCallback}">
          <TriStateCheckbox
            v-model="filterModel.value"
            @change="filterCallback()"
          />
        </template>

        <template #body="{data}">
          <div class="p-checkbox p-component">
            <Checkbox
              v-model="data.enabled"
              :binary="true"
              @click.prevent="data.enabled ? showDisable(data) : showEnable(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      v-model:visible="hasAppointmentDialogue"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('officepage.dialog.appointment.header') }}</h3>
      </template>


      <div class="confirmation-content">
        <span>{{ $t('officepage.dialog.appointment.content') }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('officepage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-rounded p-button-outlined"
          @click="abort()"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="deleteDialogue"
      :modal="true"
    >
      <template #header>
        <h3>{{ $t('officepage.dialog.delete.header') }}</h3>
      </template>

      <div class="confirmation-content">
        <span>{{ $t('officepage.dialog.delete.content') }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('officepage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="abort()"
        />
        <Button
          :label="$t('officepage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-success mr-2 p-button-outlined p-button-rounded"
          @click="deleteOffice(selected)"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="disableDialogue"
      :modal="true"
      @after-hide="setup"
    >
      <template #header>
        <h3>{{ $t('officepage.dialog.disable.header') }}</h3>
      </template>


      <div class="confirmation-content">
        <span>{{ $t('officepage.dialog.disable.content') }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('officepage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-rounded p-button-outlined"
          @click="abort()"
        />
        <Button
          :label="$t('officepage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-rounded p-button-outlined"
          @click="toggleEnabled(selected, false)"
        />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="enableDialogue"
      :modal="true"
      @after-hide="setup"
    >
      <template #header>
        <h3>{{ $t('officepage.dialog.enable.header') }}</h3>
      </template>


      <div class="confirmation-content">
        <span>{{ $t('officepage.dialog.enable.content') }}</span>
      </div>
      <template #footer>
        <Button
          :label="$t('officepage.dialog.abortButton')"
          icon="pi pi-times"
          class="p-button-rounded p-button-outlined"
          @click="abort()"
        />
        <Button
          :label="$t('officepage.dialog.confirmButton')"
          icon="pi pi-check"
          class="p-button-rounded p-button-outlined"
          @click="toggleEnabled(selected, true)"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>

import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import TriStateCheckbox from 'primevue/tristatecheckbox';
import Toolbar from 'primevue/toolbar';
import MultiSelect from 'primevue/multiselect';
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import Checkbox from 'primevue/checkbox';
import useOfficeFilter from "@/composables/filters/useOfficeFilter";
import {reactive} from "vue";
import useAppointmentFilter from "@/composables/filters/useAppointmentFilter";

export default {
  name: 'OfficeTable',
  components: { Column, DataTable, InputText, TriStateCheckbox, MultiSelect, Toolbar, Dialog, Button, Checkbox },
  setup(){
    const officeFilter = reactive(useOfficeFilter());

    const appointmentFilter = reactive(useAppointmentFilter());
    appointmentFilter.pageable.size = 1;

    return {
      officeFilter,
      appointmentFilter
    }
  },
  data() {
    return {
      sites: [],
      offices: [],
      totalOfficeCount: 0,
      isLoading: false,
      selected: null,

      disableDialogue: false,
      enableDialogue: false,
      deleteDialogue: false,
      hasAppointmentDialogue: false
    }
  },
  computed: {
    sortedSites() {
        return this.sites.slice().sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
    }
  },
  mounted() {
    this.setup()
  },
  methods: {
    async setup() {
      this.sites = (await this.$siteService.fetchSitesPagingAndSorting()).content;
      await this.getOffices();
    },
    async getOffices() {
      this.isLoading = true;
      try {
        const { content, totalElements } = await this.$officeService.getAll(this.officeFilter.getQueryParams());
        this.offices = content;
        this.totalOfficeCount = totalElements;
      } catch (e) {
        console.error(e);
        this.$emitter.emit('toast', { severity: 'error', message: 'error.global' });
      } finally {
        this.isLoading = false;
      }
    },
    onPageChange(event) {
      this.officeFilter.pageable.page = event.page;
      this.getOffices();
    },

    toOfficeForm(office = null) {
      if (office) {
        this.$router.push({ name: 'officeUpdate', params: { id: office.id } })
      } else {
        this.$router.push({ name: 'officeCreate' })
      }
    },

    abort() {
      this.disableDialogue = false
      this.enableDialogue = false
      this.deleteDialogue = false
      this.hasAppointmentDialogue = false
      this.selected = null
      this.setup()
    },

    showEnable(office) {
      this.selected = office
      this.enableDialogue = true
    },
    showDisable(office) {
      this.selected = office
      this.disableDialogue = true
    },
    showOfficeDetail(office) {
      this.$router.push({ name: 'officeDetail', params: { id: office.id } })
    },
    async showDelete(office) {
      this.appointmentFilter.filters.officeId.value = office.id
      const { totalElements } = await this.$appointmentService.getAll(this.appointmentFilter.getQueryParams());
      if (totalElements > 0) {
        this.hasAppointmentDialogue = true;
      } else {
        this.selected = office
        this.deleteDialogue = true
      }
    },

    async toggleEnabled(office, value) {
      await this.$officeService.toggleEnabled(office, value)

      if (value) {
        this.$emitter.emit('toast', { severity: 'success', message: 'officepage.toast.enable' })
      } else {
        this.$emitter.emit('toast', { severity: 'success', message: 'officepage.toast.disable' })
      }

      await this.getOffices()
      this.abort()
    },

    async deleteOffice(office) {
      await this.$officeService.delete(office)
      this.$emitter.emit('toast', { severity: 'success', message: 'officepage.toast.delete' })
      await this.getOffices()
      this.abort()
    }
  }
}

</script>

<style lang="scss" scoped>
::v-deep(.p-datatable .p-datatable-tbody td) {
  padding: .25rem 1rem;
}
</style>
